import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ChatBot from "react-chatbotify";
import "./pragathiAssistant.css";
import {
  fetchChatbotHouseDetailsThunk,
  clearGeneralMsg,
} from "../../../store/slices/generalSettingsSlice";

function PragathiAssistant() {
  const dispatch = useDispatch();
  const [selectedType, setSelectedType] = useState("");
  const [botKey, setBotKey] = useState(0);
  const [isOpen, setIsOpen] = useState(true);

  const handleClearChat = () => {
    setSelectedType("");
    dispatch(clearGeneralMsg());
    setBotKey((prev) => prev + 1);
  };

  const handleCloseBot = () => {
    handleClearChat();
    setIsOpen(false);
  };

  const handleOpenBot = () => {
    setSelectedType("");
    dispatch(clearGeneralMsg());
    setBotKey((prev) => prev + 1);
    setIsOpen(true);
  };

  const formatDMY = (value) => {
    if (!value) return "-";

    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;

    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  };

  const flow = useMemo(
    () => ({
      start: {
        message: "Hi 👋 I am Pragathi Assistant. How can I help you?",
        options: [
          "Water Bill",
          "Receipt",
          "House Details",
          "SLRM Bill",
          "Clear Chat",
        ],
        path: "handle_option",
      },

      handle_option: {
        message: ({ userInput }) => {
          if (userInput === "Clear Chat") {
            handleClearChat();
            return "Chat cleared. Please choose an option.";
          }

          setSelectedType(userInput);

          if (userInput === "Water Bill") {
            return "Enter House Code to view Water Bill details.";
          }
          if (userInput === "Receipt") {
            return "Enter House Code to view Receipt details.";
          }
          if (userInput === "House Master") {
            return "Enter House Code to view House details.";
          }
          if (userInput === "SLRM Bill") {
            return "Enter House Code to view SLRM Bill details.";
          }

          return "Please enter House Code.";
        },
        path: ({ userInput }) =>
          userInput === "Clear Chat" ? "start" : "get_house_code",
      },

      get_house_code: {
        message: async ({ userInput }) => {
          try {
            let type = "house_summary";

            if (selectedType === "Water Bill") type = "water_bill";
            if (selectedType === "Receipt") type = "receipt";
            if (selectedType === "House Master") type = "house_summary";
            if (selectedType === "SLRM Bill") type = "slrm_bill";

            const action = await dispatch(
              fetchChatbotHouseDetailsThunk({
                house_code: userInput,
                type,
              })
            );

            if (fetchChatbotHouseDetailsThunk.rejected.match(action)) {
              return action.payload || "❌ No data found for this house code.";
            }

            const result = action.payload || {};
            const data = result?.data || {};

            if (!result?.success) {
              return result?.message || "❌ No data found for this house code.";
            }

            if (type === "house_summary") {
              return [
                "🏠 House Details",
                `House Code: ${data.qr_code || userInput}`,
                `Family Head: ${data.family_head || "-"}`,
                `Door No: ${data.door_no || "-"}`,
                `Mobile: ${data.mobile || "-"}`,
                `Ward: ${data.ward || "-"}`,
                `Bill Together: ${data.slrm_water_together || "-"}`,
                `Water Rate: ${data.meter_type_name || "-"}`,
                `Slrm Rate: ${data.slrm_type_name || "-"}`,
                `Water Connection: ${data.is_water || "-"}`,
                `Slrm Connection: ${data.is_slrm || "-"}`,
                `Status: ${data.status || "-"}`,
                `Water Balance: ₹${data.water_balance ?? 0}`,
                `SLRM Balance: ₹${data.slrm_bal ?? 0}`,
                `Total Balance: ₹${data.balance ?? 0}`,
              ].join("\n");
            }

            if (type === "water_bill") {
              return [
                "💧 Water Bill Details",
                `Bill Date: ${formatDMY(data.bill_date)}`,
                `Old Reading: ${data.old_reading ?? 0}`,
                `New Reading: ${data.new_reading ?? 0}`,
                `Units: ${data.total_units ?? 0}`,
                `Old Due: ₹${data.old_due ?? 0}`,
                `Net Total: ₹${data.water_net_total ?? 0}`,
              ].join("\n");
            }

            if (type === "slrm_bill") {
              return [
                "🧹 SLRM Bill Details",
                `Bill Date: ${formatDMY(data.bill_date)}`,
                `SLRM Fee: ₹${data.slrm_fee ?? 0}`,
                `SLRM Due: ₹${data.slrm_due ?? 0}`,
                `SLRM Total: ₹${data.slrm_total ?? 0}`,
              ].join("\n");
            }

            if (type === "receipt") {
              return [
                "🧾 Receipt Details",
                `Receipt No: ${data.rct_no || "-"}`,
                `Receipt Date: ${formatDMY(data.rct_date)}`,
                `Water Fee: ₹${data.water_fee ?? 0}`,
                `SLRM Fee: ₹${data.slrm_fee ?? 0}`,
                `Amount: ₹${data.amount ?? 0}`,
                `Mode: ${data.mode || "-"}`,
                `Receipt Type: ${data.rct_type || "-"}`,


              ].join("\n");
            }

            return "✅ Data loaded successfully.";
          } catch (error) {
            console.error("chatbot house details error:", error);
            return "❌ Error fetching data.";
          } finally {
            dispatch(clearGeneralMsg());
          }
        },
        options: ["Main Menu", "Clear Chat"],
        path: "go_home",
      },

      go_home: {
        message: ({ userInput }) => {
          if (userInput === "Clear Chat") {
            handleClearChat();
            return "Chat cleared. Please choose an option.";
          }
          return "Choose another option.";
        },
        options: [
          "Water Bill",
          "Receipt",
          "House Master",
          "SLRM Bill",
          "Clear Chat",
        ],
        path: ({ userInput }) =>
          userInput === "Clear Chat" ? "start" : "handle_option",
      },
    }),
    [dispatch, selectedType]
  );

  return (
    <div className="pragathi-bot-wrapper">
      {!isOpen ? (
      <button
  type="button"
  className="pragathi-open-btn chat-bubble"
  onClick={handleOpenBot}
  title="Open Pragathi Assistant"
>
  <span className="chat-icon">💬</span>
  <span className="chat-text">Ask Pragathi</span>
  <span className="chat-dot"></span>
</button>
      ) : (
        <div className="pragathi-chat-shell">
          <ChatBot
            key={botKey}
            settings={{
              general: {
                primaryColor: "#0b2c5f",
                secondaryColor: "#f5f7fa",
                fontFamily: "'Poppins', Arial, sans-serif",
              },
              header: {
                title: "Pragathi Assistant",
              },
              chatHistory: {
                disabled: true,
              },
              tooltip: {
                mode: "NEVER",
              },
              notification: {
                disabled: true,
              },
            }}
            flow={flow}
          />

          <button
            type="button"
            className="pragathi-close-inside"
            onClick={handleCloseBot}
            title="Close Chat"
          >
            ✕
          </button>
          
        </div>
      )}
    </div>
  );
}

export default PragathiAssistant;